import React, { useId } from 'react';
import { BLOCK_ICON_NAMES, ButtonStyle, CalloutVariant, ToolDetailBlock, videoEmbedUrl } from '@easytools/tool-content';
import { ImageField } from './MediaLibrary';

const HEADING_LEVELS = [{ value: '2', label: 'Grande' }, { value: '3', label: 'Piccolo' }];
const BUTTON_STYLES = [{ value: 'primary', label: 'Pieno' }, { value: 'secondary', label: 'Bordato' }, { value: 'link', label: 'Solo testo' }];
const IMAGE_SIDES = [{ value: 'right', label: 'A destra' }, { value: 'left', label: 'A sinistra' }];
const DIVIDER_STYLES = [{ value: 'line', label: 'Riga' }, { value: 'space', label: 'Solo spazio' }];
const DIVIDER_SIZES = [{ value: 'small', label: 'Poco' }, { value: 'medium', label: 'Medio' }, { value: 'large', label: 'Molto' }];

const CALLOUT_VARIANTS = [
    { value: 'info', label: 'Info' },
    { value: 'security', label: 'Sicurezza' },
    { value: 'success', label: 'Successo' },
    { value: 'warning', label: 'Attenzione' }
];

type Patch = (patch: Partial<ToolDetailBlock>) => void;

/** Campi di un blocco, per tipo. Usato nel pannello laterale dello studio contenuti. */
const BlockFields: React.FC<{ block: ToolDetailBlock; onPatch: Patch }> = ({ block, onPatch }) => {
    switch (block.type) {
        case 'heading':
            return (
                <>
                    <SelectField label="Dimensione" value={String(block.level ?? 2)} options={HEADING_LEVELS}
                        onChange={v => onPatch({ level: Number(v) as 2 | 3 })} />
                    <TextField label="Riga sopra (facoltativa)" value={block.eyebrow ?? ''} onChange={v => onPatch({ eyebrow: v })} />
                    <TextField label="Titolo" value={block.text} onChange={v => onPatch({ text: v })} />
                    <TextArea label="Sottotitolo (facoltativo)" value={block.subtitle ?? ''} rows={2} onChange={v => onPatch({ subtitle: v })} />
                </>
            );
        case 'button':
            return (
                <>
                    <TextField label="Testo" value={block.label} onChange={v => onPatch({ label: v })} />
                    <TextField label="Dove porta" hint="https://…, mailto:…, /percorso del sito o #ancora di un blocco della pagina. Le pagine esterne si aprono in una nuova scheda."
                        value={block.url} onChange={v => onPatch({ url: v })} />
                    <SelectField label="Aspetto" value={block.style ?? 'primary'} options={BUTTON_STYLES}
                        onChange={v => onPatch({ style: v as ButtonStyle })} />
                </>
            );
        case 'mediaText':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <TextArea label="Testo" hint="**grassetto**, *corsivo*, - elenchi" value={block.markdown} rows={6} onChange={v => onPatch({ markdown: v })} />
                    <ImageField label="Immagine" value={block.url} onChange={(v, alt) => onPatch(alt ? { url: v, alt } : { url: v })} alt={block.alt} />
                    <TextField label="Testo alternativo" value={block.alt ?? ''} onChange={v => onPatch({ alt: v })} />
                    <SelectField label="Immagine" value={block.imageSide ?? 'right'} options={IMAGE_SIDES}
                        onChange={v => onPatch({ imageSide: v as 'left' | 'right' })} />
                </>
            );
        case 'quote':
            return (
                <>
                    <TextArea label="Citazione" value={block.text} rows={4} onChange={v => onPatch({ text: v })} />
                    <div className="tcs-fields__row">
                        <TextField label="Chi lo dice" value={block.author ?? ''} onChange={v => onPatch({ author: v })} />
                        <TextField label="Ruolo e azienda" value={block.role ?? ''} onChange={v => onPatch({ role: v })} />
                    </div>
                </>
            );
        case 'stats':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.items} onChange={items => onPatch({ items })}
                        empty={{ value: '', label: '' }} addLabel="Aggiungi numero" itemLabel="Numero"
                        render={(item, patch) => (
                            <div className="tcs-fields__row">
                                <TextField label="Valore" hint="es. 80%, 3 min, 12.000" value={item.value} onChange={v => patch({ value: v })} />
                                <TextField label="Cosa misura" value={item.label} onChange={v => patch({ label: v })} />
                            </div>
                        )} />
                </>
            );
        case 'cards':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.items} onChange={items => onPatch({ items })}
                        empty={{ icon: 'star', title: '', text: '' }} addLabel="Aggiungi scheda" itemLabel="Scheda"
                        render={(item, patch) => (
                            <>
                                <div className="tcs-fields__row">
                                    <IconField label="Icona" value={item.icon} onChange={v => patch({ icon: v })} />
                                    <TextField label="Titolo" value={item.title} onChange={v => patch({ title: v })} />
                                </div>
                                <TextArea label="Testo" value={item.text ?? ''} rows={2} onChange={v => patch({ text: v })} />
                                <ImageField label="Immagine al posto dell'icona (facoltativa)" value={item.image ?? ''} onChange={v => patch({ image: v })} />
                                <div className="tcs-fields__row">
                                    <TextField label="Link (facoltativo)" value={item.url ?? ''} onChange={v => patch({ url: v })} />
                                    <TextField label="Testo del link" hint="Vuoto: «Scopri di più»" value={item.linkLabel ?? ''} onChange={v => patch({ linkLabel: v })} />
                                </div>
                            </>
                        )} />
                </>
            );
        case 'video':
            return (
                <>
                    <TextField label="Indirizzo del video" hint="Un link di YouTube o Vimeo, così com'è nella barra del browser."
                        value={block.url} onChange={v => onPatch({ url: v })} />
                    {block.url.trim() && !videoEmbedUrl(block.url) && (
                        <p className="tcs-fields__error">Questo indirizzo non è un video di YouTube o Vimeo: nella pagina non comparirà.</p>
                    )}
                    <TextField label="Didascalia (facoltativa)" value={block.caption ?? ''} onChange={v => onPatch({ caption: v })} />
                </>
            );
        case 'divider':
            return (
                <div className="tcs-fields__row">
                    <SelectField label="Tipo" value={block.style ?? 'line'} options={DIVIDER_STYLES} onChange={v => onPatch({ style: v as 'line' | 'space' })} />
                    <SelectField label="Spazio" value={block.size ?? 'medium'} options={DIVIDER_SIZES} onChange={v => onPatch({ size: v as 'small' | 'medium' | 'large' })} />
                </div>
            );
        case 'logos':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.items} onChange={items => onPatch({ items })}
                        empty={{ url: '', alt: '' }} addLabel="Aggiungi logo" itemLabel="Logo"
                        render={(item, patch) => (
                            <>
                                <ImageField label="Logo" value={item.url} onChange={(v, alt) => patch(alt ? { url: v, alt } : { url: v })} alt={item.alt} />
                                <div className="tcs-fields__row">
                                    <TextField label="Nome" value={item.alt} onChange={v => patch({ alt: v })} />
                                    <TextField label="Link (facoltativo)" value={item.href ?? ''} onChange={v => patch({ href: v })} />
                                </div>
                            </>
                        )} />
                </>
            );
        case 'hero':
            return (
                <>
                    <TextArea label="Frase di apertura" value={block.tagline ?? ''} rows={3}
                        onChange={v => onPatch({ tagline: v })} />
                    <StringList label="Punti in evidenza" values={block.highlights ?? []}
                        placeholder="es. nessuna installazione" onChange={v => onPatch({ highlights: v })} />
                </>
            );
        case 'richText':
            return (
                <TextArea label="Testo" hint="**grassetto**, *corsivo*, `codice`, - elenchi, ## titoli"
                    value={block.markdown} rows={12} onChange={v => onPatch({ markdown: v })} />
            );
        case 'callout':
            return (
                <>
                    <div className="tcs-fields__row">
                        <SelectField label="Tipo" value={block.variant ?? 'info'} options={CALLOUT_VARIANTS}
                            onChange={v => onPatch({ variant: v as CalloutVariant })} />
                        <IconField label="Icona" value={block.icon} onChange={v => onPatch({ icon: v })} />
                    </div>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <TextArea label="Testo" value={block.text} rows={4} onChange={v => onPatch({ text: v })} />
                </>
            );
        case 'chipList':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <StringList label="Pillole" values={block.chips} placeholder="es. Entità e campi"
                        onChange={v => onPatch({ chips: v })} />
                </>
            );
        case 'featureGrid':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.items} onChange={items => onPatch({ items })}
                        empty={{ icon: 'star', title: '', text: '' }} addLabel="Aggiungi funzionalità" itemLabel="Funzionalità"
                        render={(item, patch) => (
                            <>
                                <div className="tcs-fields__row">
                                    <IconField label="Icona" value={item.icon} onChange={v => patch({ icon: v })} />
                                    <TextField label="Titolo" value={item.title} onChange={v => patch({ title: v })} />
                                </div>
                                <TextArea label="Testo" value={item.text ?? ''} rows={2} onChange={v => patch({ text: v })} />
                            </>
                        )} />
                </>
            );
        case 'steps':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.items} onChange={items => onPatch({ items })}
                        empty={{ title: '', text: '' }} addLabel="Aggiungi passo" itemLabel="Passo"
                        render={(item, patch) => (
                            <>
                                <TextField label="Titolo" value={item.title} onChange={v => patch({ title: v })} />
                                <TextArea label="Testo" value={item.text ?? ''} rows={2} onChange={v => patch({ text: v })} />
                            </>
                        )} />
                </>
            );
        case 'specs':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.rows} onChange={rows => onPatch({ rows })}
                        empty={{ label: '', value: '' }} addLabel="Aggiungi riga" itemLabel="Riga"
                        render={(row, patch) => (
                            <div className="tcs-fields__row">
                                <TextField label="Etichetta" value={row.label} onChange={v => patch({ label: v })} />
                                <TextField label="Valore" value={row.value} onChange={v => patch({ value: v })} />
                            </div>
                        )} />
                </>
            );
        case 'faq':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.items} onChange={items => onPatch({ items })}
                        empty={{ q: '', a: '' }} addLabel="Aggiungi domanda" itemLabel="Domanda"
                        render={(item, patch) => (
                            <>
                                <TextField label="Domanda" value={item.q} onChange={v => patch({ q: v })} />
                                <TextArea label="Risposta" value={item.a} rows={3} onChange={v => patch({ a: v })} />
                            </>
                        )} />
                </>
            );
        case 'media':
            return (
                <>
                    <ImageField label="Immagine" value={block.url} onChange={(v, alt) => onPatch(alt ? { url: v, alt } : { url: v })} alt={block.alt} />
                    <TextField label="Testo alternativo" value={block.alt ?? ''} onChange={v => onPatch({ alt: v })} />
                    <TextField label="Didascalia" value={block.caption ?? ''} onChange={v => onPatch({ caption: v })} />
                </>
            );
        case 'gallery':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <ItemList items={block.images} onChange={images => onPatch({ images })}
                        empty={{ url: '', alt: '', caption: '' }} addLabel="Aggiungi schermata" itemLabel="Schermata"
                        render={(image, patch) => (
                            <>
                                <ImageField label="Schermata" value={image.url} onChange={(v, alt) => patch(alt ? { url: v, alt } : { url: v })} alt={image.alt} />
                                <TextField label="Testo alternativo" value={image.alt ?? ''} onChange={v => patch({ alt: v })} />
                                <TextField label="Didascalia" value={image.caption ?? ''} onChange={v => patch({ caption: v })} />
                            </>
                        )} />
                </>
            );
        case 'cta':
            return (
                <>
                    <TextField label="Titolo" value={block.title} onChange={v => onPatch({ title: v })} />
                    <TextArea label="Testo (facoltativo)" value={block.text ?? ''} rows={2} onChange={v => onPatch({ text: v })} />
                    <TextField label="Testo del pulsante" hint="Vuoto: «Entra in Semplora». Il pulsante porta all'accesso."
                        value={block.label ?? ''} onChange={v => onPatch({ label: v })} />
                </>
            );
        case 'related':
            return (
                <>
                    <TextField label="Titolo (facoltativo)" hint="Vuoto: «Altri tool che potrebbero servirti»"
                        value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
                    <p className="tcs-fields__note">L'elenco dei tool si compone da solo: gli altri tool pubblicati della stessa categoria.</p>
                </>
            );
        case 'section':
            return <TextField label="Titolo della sezione" value={block.title} onChange={v => onPatch({ title: v })} />;
        case 'table':
            return <TableFields block={block} onPatch={onPatch} />;
        case 'pageBreak':
            return <p className="tcs-fields__note">Nel PDF della guida il blocco successivo inizia su una nuova pagina.</p>;
        default:
            return null;
    }
};

const TableFields: React.FC<{ block: Extract<ToolDetailBlock, { type: 'table' }>; onPatch: Patch }> = ({ block, onPatch }) => {
    const columns = Math.max(1, block.head.length);
    const resize = (count: number) => onPatch({
        head: Array.from({ length: count }, (_, i) => block.head[i] ?? ''),
        rows: block.rows.map(row => Array.from({ length: count }, (_, i) => row[i] ?? '')),
    });
    return (
        <>
            <TextField label="Titolo (facoltativo)" value={block.title ?? ''} onChange={v => onPatch({ title: v })} />
            <div className="tcs-fields__inline">
                <span className="tcs-fields__label">Colonne: {columns}</span>
                <button type="button" className="btn btn-sm btn-light" disabled={columns <= 1} onClick={() => resize(columns - 1)}>Togli colonna</button>
                <button type="button" className="btn btn-sm btn-light" disabled={columns >= 6} onClick={() => resize(columns + 1)}>Aggiungi colonna</button>
            </div>
            <div className="tcs-fields__grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {block.head.map((cell, i) => (
                    <input key={`h${i}`} className="form-control form-control-sm fw-semibold" aria-label={`Intestazione colonna ${i + 1}`}
                        value={cell} placeholder={`Intestazione ${i + 1}`}
                        onChange={e => onPatch({ head: block.head.map((v, idx) => idx === i ? e.target.value : v) })} />
                ))}
                {block.rows.map((row, r) => row.map((cell, c) => (
                    <input key={`r${r}c${c}`} className="form-control form-control-sm" aria-label={`Riga ${r + 1}, colonna ${c + 1}`}
                        value={cell}
                        onChange={e => onPatch({ rows: block.rows.map((rr, ri) => ri === r ? rr.map((v, ci) => ci === c ? e.target.value : v) : rr) })} />
                )))}
            </div>
            <div className="tcs-fields__inline">
                <button type="button" className="btn btn-sm btn-light" onClick={() => onPatch({ rows: [...block.rows, Array(columns).fill('')] })}>Aggiungi riga</button>
                <button type="button" className="btn btn-sm btn-light" disabled={block.rows.length <= 1} onClick={() => onPatch({ rows: block.rows.slice(0, -1) })}>Togli l'ultima riga</button>
            </div>
        </>
    );
};

// ─── Controlli di campo ──────────────────────────────────────────────────────

const Field: React.FC<{ label: string; hint?: string; id: string; children: React.ReactNode }> = ({ label, hint, id, children }) => (
    <div className="tcs-fields__field">
        <label className="tcs-fields__label" htmlFor={id}>{label}</label>
        {children}
        {hint && <div className="tcs-fields__hint">{hint}</div>}
    </div>
);

const TextField: React.FC<{ label: string; hint?: string; value: string; onChange: (v: string) => void }> = ({ label, hint, value, onChange }) => {
    const id = useId();
    return <Field label={label} hint={hint} id={id}><input id={id} className="form-control form-control-sm" value={value} onChange={e => onChange(e.target.value)} /></Field>;
};

const TextArea: React.FC<{ label: string; hint?: string; value: string; rows?: number; onChange: (v: string) => void }> = ({ label, hint, value, rows = 3, onChange }) => {
    const id = useId();
    return <Field label={label} hint={hint} id={id}><textarea id={id} className="form-control form-control-sm" rows={rows} value={value} onChange={e => onChange(e.target.value)} /></Field>;
};

const SelectField: React.FC<{ label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => {
    const id = useId();
    return (
        <Field label={label} id={id}>
            <select id={id} className="form-select form-select-sm" value={value} onChange={e => onChange(e.target.value)}>
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </Field>
    );
};

const IconField: React.FC<{ label: string; value?: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
    <SelectField label={label} value={value ?? ''} onChange={onChange}
        options={[{ value: '', label: 'Nessuna' }, ...BLOCK_ICON_NAMES.map(name => ({ value: name, label: name }))]} />
);

const StringList: React.FC<{ label: string; values: string[]; placeholder?: string; onChange: (v: string[]) => void }> = ({ label, values, placeholder, onChange }) => (
    <div className="tcs-fields__field">
        <span className="tcs-fields__label">{label}</span>
        {values.map((val, i) => (
            <div key={i} className="input-group input-group-sm mb-1">
                <input className="form-control" value={val} placeholder={placeholder} aria-label={`${label} ${i + 1}`}
                    onChange={e => onChange(values.map((v, idx) => (idx === i ? e.target.value : v)))} />
                <button type="button" className="btn btn-outline-secondary" aria-label={`Elimina ${label.toLowerCase()} ${i + 1}`}
                    onClick={() => onChange(values.filter((_, idx) => idx !== i))}><i className="bi bi-x-lg" /></button>
            </div>
        ))}
        <button type="button" className="btn btn-sm btn-light" onClick={() => onChange([...values, ''])}>
            <i className="bi bi-plus-lg me-1" />Aggiungi
        </button>
    </div>
);

/** Lista di elementi (funzionalità, passi, righe, domande, schermate). */
function ItemList<T>({ items, onChange, empty, addLabel, itemLabel, render }: {
    items: T[];
    onChange: (items: T[]) => void;
    empty: T;
    addLabel: string;
    itemLabel: string;
    render: (item: T, patch: (p: Partial<T>) => void) => React.ReactNode;
}) {
    const patchItem = (index: number, p: Partial<T>) => onChange(items.map((it, i) => (i === index ? { ...it, ...p } : it)));
    const move = (index: number, dir: -1 | 1) => {
        const next = [...items];
        [next[index], next[index + dir]] = [next[index + dir], next[index]];
        onChange(next);
    };
    return (
        <div className="tcs-items">
            {items.map((item, i) => (
                <fieldset key={i} className="tcs-items__item">
                    <legend className="tcs-items__head">
                        <span>{itemLabel} {i + 1}</span>
                        <span className="tcs-items__actions">
                            <button type="button" className="btn btn-sm btn-link" disabled={i === 0} aria-label={`Sposta su ${itemLabel.toLowerCase()} ${i + 1}`} onClick={() => move(i, -1)}><i className="bi bi-arrow-up" /></button>
                            <button type="button" className="btn btn-sm btn-link" disabled={i === items.length - 1} aria-label={`Sposta giù ${itemLabel.toLowerCase()} ${i + 1}`} onClick={() => move(i, 1)}><i className="bi bi-arrow-down" /></button>
                            <button type="button" className="btn btn-sm btn-link text-danger" aria-label={`Elimina ${itemLabel.toLowerCase()} ${i + 1}`} onClick={() => onChange(items.filter((_, idx) => idx !== i))}><i className="bi bi-trash" /></button>
                        </span>
                    </legend>
                    {render(item, p => patchItem(i, p))}
                </fieldset>
            ))}
            <button type="button" className="btn btn-sm btn-light align-self-start" onClick={() => onChange([...items, { ...empty }])}>
                <i className="bi bi-plus-lg me-1" />{addLabel}
            </button>
        </div>
    );
}

export default BlockFields;
